/**
 * Project service — phases and milestones for a single project.
 *
 * Used by ProjectRow (ProjectListSection) to lazy-load data for the
 * home-level project list.  The outline engine's loadProjectData() uses
 * the same table IDs and filter formulas — this service replicates them
 * through the service layer so React components don't call the engine
 * directly.
 *
 * Note: Do NOT add fields[] restrictions to these queries.  Several fields
 * on both tables are multipleLookupValues and return empty silently when
 * fields[] is used.
 */

import { apiGet, apiGetAll, apiGetOne, apiPatch } from './_api.js'

const TBL_PROJ = 'tblgWX330MPJkhQVq'   // Projects
const TBL_PH   = 'tblNl3JpfIEWk9tPX'  // Phases
const TBL_DEL  = 'tblFuiDpahqCL4eIK'  // Project Deliverables (milestones)

/**
 * Every Executed or On Hold project, sorted by Project Number — the home project list's own
 * source (outline.js#loadProjects). Uses apiGetAll (proper offset pagination), not a capped single
 * page: a fixed `maxRecords=50` here previously truncated the list once the firm passed 50 active+
 * on-hold projects, silently dropping whichever sorted last — in practice the newest project by
 * number (e.g. Dolle Residence, 26-15, reported missing 2026-08-28 once the count hit 52).
 * @returns {Promise<Array>}  Raw Airtable project records
 */
export function getOpenProjects() {
  const formula = 'OR({Contract Stage}="Executed",{Contract Stage}="On Hold")'
  return apiGetAll(
    TBL_PROJ,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Project+Number&sort%5B0%5D%5Bdirection%5D=asc'
  )
}

/**
 * Fetch the full field set for a single project record.
 * No fields[] restriction — lookup/formula fields return empty silently when filtered.
 * @param {string} projectId  Airtable Projects record ID
 * @returns {Promise<{id, fields}|null>}  Full Airtable record or null
 */
export function getProjectDetails(projectId) {
  return apiGetOne(TBL_PROJ, projectId)
}

/**
 * PATCH one or more fields on a project record.
 * Used for inline edits in ProjectInfoModal.
 * @param {string} projectId  Airtable Projects record ID
 * @param {object} fields     Map of field name → new value
 */
export function updateProjectDetails(projectId, fields) {
  return apiPatch(TBL_PROJ, projectId, fields)
}

/**
 * Fetch all phases for a project, sorted by Sort Order.
 * @param {string} projectId  Airtable Projects record ID
 * @returns {Promise<Array>}  Raw Airtable phase records
 */
export function getProjectPhases(projectId, opts) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGet(
    TBL_PH,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=20',
    opts
  )
}

/**
 * Fetch all milestones for a project, sorted by Latest Finish (LFT).
 * Scoped purely by projectId, not by the project's Contract Stage — once a
 * user has navigated into one specific project (this row expanded, or its own
 * zoomed page via outline.js#loadProjectData), its real milestones should show
 * regardless of On Hold/Completed/Cancelled status. The Executed-only gate is
 * for cross-project aggregate views (scheduler, billing, /overdue, etc.),
 * which read from other services — see docs/PROJECT_CLOSEOUT.md.
 * @param {string} projectId  Airtable Projects record ID
 * @returns {Promise<Array>}  Raw Airtable milestone records
 */
/** Every unposted milestone on a project, all pages (getProjectMilestones stops at 50). */
export function getProjectOpenMilestones(projectId) {
  const formula = 'AND(FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},",")),{Posted Date}=BLANK())'
  return apiGetAll(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=asc'
  )
}

export function getProjectMilestones(projectId, opts) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))'
  return apiGet(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=50',
    opts
  )
}
